<template>
  <registry-page
    title="Расчетные счета"
    description="Банковские реквизиты организации для платежей и бухгалтерских документов."
  >
    <app-data-table
      data-testid="account-directory-table"
      title="Банковские счета"
      storage-key="account-directory-table"
      :count="filteredRows.length"
      :rows="filteredRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-message="errorMessage"
      @update:pagination="pagination = $event"
      @reset="resetFilters"
      @retry="loadAccounts"
    >
      <template #filters>
        <filter-bar
          :model-value="search"
          search-label="Поиск по банку, счету или БИК"
          search-test-id="account-directory-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="applySearch"
          @reset="resetFilters"
          @remove="removeFilter"
        >
          <template #quick>
            <q-select
              dense
              outlined
              emit-value
              map-options
              label="Статус"
              class="account-status-filter"
              :model-value="statusFilter"
              :options="statusOptions"
              data-testid="account-directory-status"
              @update:model-value="applyStatus"
            />
          </template>
          <template #advanced>
            <q-select
              outlined
              emit-value
              map-options
              label="Статус"
              :model-value="statusFilter"
              :options="statusOptions"
              @update:model-value="applyStatus"
            />
          </template>
        </filter-bar>
      </template>

      <template v-if="canEditFinance" #actions>
        <q-btn
          ref="addButton"
          unelevated
          color="primary"
          icon="add"
          no-caps
          label="Добавить"
          data-testid="account-directory-add"
          @click="openCreateDialog"
        />
      </template>

      <template #body-cell-bank_name="props">
        <q-td :props="props">
          <div class="account-name">{{ props.row.bank_name || 'Банк не указан' }}</div>
          <div class="text-caption text-grey-7">БИК {{ props.row.bik || 'не указан' }}</div>
        </q-td>
      </template>

      <template #body-cell-account_number="props">
        <q-td :props="props" class="account-number">{{ props.row.account_number || '—' }}</q-td>
      </template>

      <template #body-cell-status="props">
        <q-td :props="props">
          <status-badge
            :tone="props.row.is_active === false ? 'neutral' : 'success'"
            :icon="props.row.is_active === false ? 'pause_circle' : 'check_circle'"
            :label="props.row.is_active === false ? 'Неактивен' : 'Активен'"
          />
        </q-td>
      </template>

      <template #body-cell-actions="props">
        <q-td :props="props" class="text-right">
          <q-btn
            v-if="canEditFinance"
            flat
            round
            dense
            color="primary"
            icon="edit"
            aria-label="Редактировать счет"
            data-testid="account-directory-edit"
            :disable="pending"
            @click.stop="openEditDialog(props.row)"
          />
          <q-btn
            v-if="canEditFinance"
            flat
            round
            dense
            color="negative"
            icon="delete"
            aria-label="Удалить счет"
            data-testid="account-directory-delete"
            :disable="pending"
            @click.stop="openDeleteDialog(props.row)"
          />
        </q-td>
      </template>
    </app-data-table>

    <q-dialog v-model="formDialog" :persistent="pending" @hide="restoreFormFocus">
      <q-card class="directory-dialog" @keydown.esc="closeFormDialog">
        <q-card-section class="row items-center">
          <h2 class="directory-dialog__title">{{ editMode ? 'Редактировать счет' : 'Новый расчетный счет' }}</h2>
          <q-space />
          <q-btn flat round dense icon="close" aria-label="Закрыть" :disable="pending" @click="closeFormDialog" />
        </q-card-section>
        <q-separator />
        <q-card-section>
          <q-banner v-if="formError" class="bg-red-1 text-negative q-mb-md" rounded data-testid="account-directory-form-error">
            {{ formError }}
          </q-banner>
          <form-grid>
            <q-input ref="bankName" v-model.trim="form.bank_name" dense outlined label="Банк" data-testid="account-directory-bank" :disable="pending" />
            <q-input v-model.trim="form.bik" dense outlined label="БИК" data-testid="account-directory-bik" :disable="pending" />
            <q-input v-model.trim="form.account_number" dense outlined label="Расчетный счет" data-testid="account-directory-number" :disable="pending" />
            <q-input v-model.trim="form.kor_bill" dense outlined label="Корреспондентский счет" data-testid="account-directory-correspondent" :disable="pending" />
            <q-toggle v-model="form.is_active" label="Активный счет" :disable="pending" />
          </form-grid>
        </q-card-section>
        <q-card-actions align="right" class="directory-dialog__actions">
          <q-btn flat no-caps label="Отмена" :disable="pending" @click="closeFormDialog" />
          <q-btn
            unelevated
            no-caps
            color="primary"
            label="Сохранить"
            data-testid="account-directory-save"
            :loading="pending"
            @click="saveAccount"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <confirm-dialog
      v-model="deleteDialog"
      title="Удалить расчетный счет"
      :body="deleteDialogBody"
      confirm-label="Удалить"
      destructive
      :pending="pending"
      @cancel="cancelDelete"
      @confirm="deleteAccount"
    />
  </registry-page>
</template>

<script>
import {
  AppDataTable,
  ConfirmDialog,
  FilterBar,
  FormGrid,
  RegistryPage,
  StatusBadge
} from '../../../components/ui'

function emptyForm() {
  return {
    id: null,
    bank_name: '',
    account_number: '',
    bik: '',
    kor_bill: '',
    is_active: true
  }
}

export default {
  name: 'OrgAccountsList',
  components: {
    AppDataTable,
    ConfirmDialog,
    FilterBar,
    FormGrid,
    RegistryPage,
    StatusBadge
  },
  data() {
    const query = this.$route.query || {}
    return {
      rows: [],
      loading: false,
      accessDenied: false,
      errorMessage: '',
      formError: '',
      requestId: null,
      mutationRequestId: null,
      pending: false,
      formDialog: false,
      deleteDialog: false,
      editMode: false,
      selectedRow: null,
      formReturnFocus: null,
      form: emptyForm(),
      search: String(query.q || ''),
      statusFilter: String(query.status || 'active'),
      pagination: { page: 1, rowsPerPage: 20, rowsNumber: 0, sortBy: 'bank_name', descending: false },
      statusOptions: [
        { label: 'Активные', value: 'active' },
        { label: 'Неактивные', value: 'inactive' },
        { label: 'Все', value: 'all' }
      ],
      columns: [
        { name: 'bank_name', align: 'left', label: 'Банк', field: 'bank_name', sortable: true, required: true },
        { name: 'account_number', align: 'left', label: 'Расчетный счет', field: 'account_number', sortable: true, required: true },
        { name: 'kor_bill', align: 'left', label: 'Корр. счет', field: 'kor_bill', classes: 'column-priority-secondary', headerClasses: 'column-priority-secondary' },
        { name: 'status', align: 'left', label: 'Статус', field: 'is_active' },
        { name: 'actions', align: 'right', label: 'Действия', field: 'id', required: true }
      ],
      handlers: []
    }
  },
  computed: {
    canEditFinance() {
      return this.$store.state.permissions.edit_fin_documents === true
    },
    hasFilters() {
      return Boolean(this.search || this.statusFilter !== 'active')
    },
    appliedFilters() {
      const filters = []
      if (this.search) filters.push({ key: 'search', label: `Поиск: ${this.search}` })
      if (this.statusFilter !== 'active') {
        const option = this.statusOptions.find((item) => item.value === this.statusFilter)
        filters.push({ key: 'status', label: `Статус: ${option ? option.label : this.statusFilter}` })
      }
      return filters
    },
    filteredRows() {
      const search = this.search.trim().toLocaleLowerCase('ru')
      return this.rows.filter((row) => {
        const active = row.is_active !== false
        if (this.statusFilter === 'active' && !active) return false
        if (this.statusFilter === 'inactive' && active) return false
        if (!search) return true
        return [row.bank_name, row.account_number, row.bik, row.kor_bill]
          .some((value) => String(value || '').toLocaleLowerCase('ru').includes(search))
      })
    },
    tableState() {
      if (this.accessDenied) return 'access-denied'
      if (this.errorMessage) return 'error'
      if (this.loading && this.rows.length === 0) return 'loading'
      if (this.filteredRows.length === 0) return this.hasFilters ? 'filtered-empty' : 'empty'
      return 'ready'
    },
    deleteDialogBody() {
      const row = this.selectedRow || {}
      return `Счет ${row.account_number || ''} в банке «${row.bank_name || 'Без названия'}» будет удален без возможности восстановления.`
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        this.search = String(query.q || '')
        this.statusFilter = String(query.status || 'active')
      }
    }
  },
  mounted() {
    this.bind('onGetOrgAccountsList', this.onAccounts)
    this.bind('onCreateOrgAccount', this.onMutation)
    this.bind('onUpdateOrgAccount', this.onMutation)
    this.bind('onDeleteOrgAccount', this.onMutation)
    this.bind('accessDeniedErrorReceived', this.onAccessDenied)
    const ready = () => this.loadAccounts()
    this.handlers.push(['connectionReady', ready])
    if (this.connection.isAuthenticated) ready()
    else this.emitter.on('connectionReady', ready)
  },
  beforeUnmount() {
    this.handlers.forEach(([name, handler]) => this.emitter.off(name, handler))
  },
  methods: {
    bind(name, handler) {
      this.handlers.push([name, handler])
      this.emitter.on(name, handler)
    },
    pushQuery(changes) {
      const query = { ...this.$route.query, ...changes }
      Object.keys(query).forEach((key) => {
        if (query[key] === null || query[key] === undefined || query[key] === '') delete query[key]
      })
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.push({ query })
    },
    applySearch(value) {
      this.pushQuery({ q: value || null })
    },
    applyStatus(value) {
      this.pushQuery({ status: value === 'active' ? null : value })
    },
    removeFilter(key) {
      if (key === 'search') this.applySearch('')
      if (key === 'status') this.applyStatus('active')
    },
    resetFilters() {
      this.pushQuery({ q: null, status: null })
    },
    loadAccounts() {
      this.loading = true
      this.errorMessage = ''
      this.accessDenied = false
      this.requestId = this.connection.makeRequestId('accounts')
      this.connection.getOrgAccountsList(this.requestId)
    },
    onAccounts(response) {
      if (response.request_id !== this.requestId) return
      this.loading = false
      if (response.type === 'error' || response.error) {
        this.rows = []
        this.accessDenied = response.error === 'access_denied'
        this.errorMessage = this.accessDenied ? '' : (response.message || 'Не удалось загрузить расчетные счета')
        return
      }
      this.rows = Array.isArray(response.data) ? response.data : []
      this.pagination = { ...this.pagination, rowsNumber: this.rows.length }
    },
    onAccessDenied(response) {
      if (response.request_id === this.mutationRequestId) {
        this.pending = false
        if (this.formDialog) this.formError = 'Недостаточно прав для изменения расчетного счета'
        else this.errorMessage = 'Недостаточно прав для удаления расчетного счета'
        return
      }
      if (response.request_id !== this.requestId) return
      this.loading = false
      this.rows = []
      this.accessDenied = true
      this.errorMessage = ''
    },
    openCreateDialog() {
      this.formReturnFocus = (this.$refs.addButton && this.$refs.addButton.$el) || document.activeElement
      this.editMode = false
      this.form = emptyForm()
      this.formError = ''
      this.formDialog = true
    },
    openEditDialog(row) {
      this.formReturnFocus = document.activeElement
      this.editMode = true
      this.form = { ...emptyForm(), ...row, is_active: row.is_active !== false }
      this.formError = ''
      this.formDialog = true
    },
    closeFormDialog() {
      if (!this.pending) this.formDialog = false
    },
    restoreFormFocus() {
      const target = this.formReturnFocus
      this.formReturnFocus = null
      window.setTimeout(() => {
        if (target && document.contains(target)) target.focus({ preventScroll: true })
      }, 0)
    },
    saveAccount() {
      if (this.pending) return
      const required = ['bank_name', 'account_number', 'bik', 'kor_bill']
      if (required.some((key) => !String(this.form[key] || '').trim())) {
        this.formError = 'Заполните банк, БИК, расчетный и корреспондентский счета'
        this.$nextTick(() => this.$refs.bankName && this.$refs.bankName.focus())
        return
      }
      this.pending = true
      this.formError = ''
      this.mutationRequestId = this.connection.makeRequestId(this.editMode ? 'account-update' : 'account-create')
      const args = [
        this.mutationRequestId,
        this.form.bank_name,
        this.form.account_number,
        this.form.bik,
        this.form.kor_bill,
        this.form.is_active
      ]
      if (this.editMode) this.connection.updateOrgAccount(args[0], this.form.id, ...args.slice(1))
      else this.connection.createOrgAccount(...args)
    },
    openDeleteDialog(row) {
      this.selectedRow = row
      this.deleteDialog = true
    },
    cancelDelete() {
      if (!this.pending) {
        this.deleteDialog = false
        this.selectedRow = null
      }
    },
    deleteAccount() {
      if (this.pending || !this.selectedRow) return
      this.pending = true
      this.mutationRequestId = this.connection.makeRequestId('account-delete')
      this.connection.deleteOrgAccount(this.mutationRequestId, this.selectedRow.id)
    },
    onMutation(response) {
      if (response.request_id !== this.mutationRequestId) return
      this.pending = false
      if (response.type === 'error' || response.error) {
        const message = response.message || 'Не удалось выполнить операцию со счетом'
        if (this.formDialog) this.formError = message
        else this.errorMessage = message
        return
      }
      const deleted = response.action === 'delete_org_account'
      this.formDialog = false
      this.deleteDialog = false
      this.selectedRow = null
      this.$q.notify({ color: 'positive', message: deleted ? 'Расчетный счет удален' : 'Расчетный счет сохранен' })
      this.loadAccounts()
    }
  }
}
</script>

<style scoped>
.account-status-filter { min-width: 11rem; }
.account-name { max-width: 32rem; overflow-wrap: anywhere; font-weight: var(--sid-font-weight-medium); }
.account-number { font-variant-numeric: tabular-nums; white-space: nowrap; }
.directory-dialog { width: min(46rem, calc(100vw - 2 * var(--sid-space-4))); max-width: 100%; border-radius: var(--sid-radius-large); }
.directory-dialog__title { margin: 0; font-size: var(--sid-font-size-section); line-height: var(--sid-line-height-section); }
.directory-dialog__actions { gap: var(--sid-space-2); padding: 0 var(--sid-space-4) var(--sid-space-4); }

@media (max-width: 599px) {
  .directory-dialog__actions { align-items: stretch; flex-direction: column-reverse; }
  .directory-dialog__actions :deep(.q-btn) { width: 100%; min-height: var(--sid-touch-target); }
}
</style>
